import queryString from "query-string";

import type { ApiError } from "@/core/lib/api-errors";
import {
  extractApiExceptionCodeFromData,
  isAbortError,
  isHttpValidationError,
  isMissingPermissionsError,
  isServiceUnavailableApiExceptionResponse,
  toApiError,
} from "@/core/lib/api-errors";
import { config } from "@/core/lib/app-config";
import { saveAuthRedirect } from "@/core/lib/auth-redirect";
import {
  authCurrentLocation,
  redirectToSignIn,
  redirectToTerms,
} from "@/core/lib/auth-router";
import {
  clearAuthSession,
  readAuthSession,
  setTokenExpiry,
} from "@/core/lib/auth-session";
import {
  getFaroSubmissionContext,
  reportValidationError,
} from "@/core/lib/observability";
import { queryClient } from "@/core/lib/query-client";
import { addToast } from "@/core/lib/toast";
import { handleUsageLimitExceededError } from "@/core/lib/usage-limit";
import { jwtRefresh, workosRefresh } from "@/openapi";
import {
  getCurrentUserInvitationsQueryKey,
  getCurrentUserPermissionQueryKey,
  getCurrentUserQueryKey,
} from "@/openapi/@tanstack/react-query.gen";
import { client } from "@/openapi/client.gen";
import { m } from "@/paraglide/messages";

// refresh 30 seconds before expiry to avoid races
const REFRESH_BUFFER_MS = 30_000;
// web lock name shared by all tabs so only one refresh runs browser-wide;
// refresh tokens are single-use, a concurrent second refresh gets rejected
const REFRESH_LOCK_NAME = "auth-token-refresh";

client.setConfig({
  baseUrl: config.backendUrl,
  credentials: "include",
  querySerializer: (params) => {
    const serialized = queryString.stringify(params, {
      skipNull: true,
      skipEmptyString: true,
    });
    return serialized;
  },
});

/**
 * A fetch that gives up after `ms`, for the calls the app cannot start without
 * and so must fail fast rather than hang. The generated query functions hand
 * the query's own `signal` to the request, replacing any passed in options, so
 * the timeout rides on the transport instead.
 */
export function fetchWithTimeout(ms: number): typeof fetch {
  return (input, init) => {
    const request = new Request(input, init);
    return fetch(request, {
      signal: AbortSignal.any([request.signal, AbortSignal.timeout(ms)]),
    });
  };
}

function isExpiring(tokenExpiresAt: string | null): boolean {
  if (!tokenExpiresAt) return false;

  const expiresAtMs = new Date(tokenExpiresAt).getTime();
  if (Number.isNaN(expiresAtMs)) return false;

  return Date.now() >= expiresAtMs - REFRESH_BUFFER_MS;
}

// browser-wide mutex across tabs; falls back to running directly where
// the web locks api is unavailable (the per-tab dedupe still applies)
function withRefreshLock(fn: () => Promise<void>): Promise<void> {
  if (navigator.locks) {
    return navigator.locks.request(REFRESH_LOCK_NAME, fn);
  }
  return fn();
}

let refreshPromise: Promise<void> | null = null;

function doRefresh(): Promise<void> {
  if (!refreshPromise) {
    const { authMethod } = readAuthSession();
    const refreshFn =
      authMethod === "workos"
        ? workosRefresh({ throwOnError: true })
        : jwtRefresh({ throwOnError: true });

    refreshPromise = refreshFn
      .then((res) => {
        const expiresIn = res.data?.expires_in;
        if (typeof expiresIn === "number") {
          setTokenExpiry(expiresIn);
        }
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

async function ensureFreshToken({ force = false }: { force?: boolean } = {}) {
  const expiryBefore = readAuthSession().tokenExpiresAt;
  if (!force && !isExpiring(expiryBefore)) return;

  await withRefreshLock(async () => {
    // re-check under the lock: another tab may have refreshed while we
    // waited, in which case the fresh token is already in the cookie jar
    const expiryNow = readAuthSession().tokenExpiresAt;
    const renewedWhileWaiting =
      expiryNow !== expiryBefore && !isExpiring(expiryNow);
    if (force ? renewedWhileWaiting : !isExpiring(expiryNow)) return;

    await doRefresh();
  });
}

// the token refresh calls themselves, which must neither wait on a refresh nor
// be replayed after one. matched on the end of the path so a subpath base still
// works, and not as a substring: product endpoints such as dashboard refresh
// end in /refresh too, and are ordinary authenticated requests
const TOKEN_REFRESH_PATHS = ["/auth/jwt/refresh", "/auth/workos/refresh"];

function isTokenRefresh(request: Request): boolean {
  const { pathname } = new URL(request.url);
  return TOKEN_REFRESH_PATHS.some((path) => pathname.endsWith(path));
}

// counts sign-outs, so a request can be stamped with the session it was issued
// in and its 401 recognised as belonging to a session a sign-out already ended
let signOuts = 0;

// what the later interceptors need about a request that went out: a copy to
// replay (sending consumes the body) and the session it was issued in. keyed by
// the request the response and error interceptors are handed back
const issued = new WeakMap<Request, { replay: Request; session: number }>();

client.interceptors.request.use(async (request) => {
  // taken before the refresh below, which can itself end the session
  const session = signOuts;
  if (!isTokenRefresh(request)) {
    try {
      await ensureFreshToken();
    } catch {
      // refresh failed: the response interceptor will retry or redirect
    }
  }
  // backend logs bind it, so a session seen in browser telemetry can be
  // followed into the requests it made
  const faroSessionId = getFaroSubmissionContext().faro_session_id;
  if (faroSessionId) request.headers.set("X-Faro-Session-Id", faroSessionId);
  issued.set(request, { replay: request.clone(), session });
  return request;
});

function issuedBeforeLastSignOut(request: Request): boolean {
  const entry = issued.get(request);
  return entry !== undefined && entry.session !== signOuts;
}

function clearAuthAndRedirect() {
  signOuts += 1;
  clearAuthSession();
  queryClient.removeQueries({ queryKey: getCurrentUserQueryKey() });
  queryClient.removeQueries({
    queryKey: getCurrentUserInvitationsQueryKey(),
  });
  queryClient.removeQueries({
    queryKey: getCurrentUserPermissionQueryKey(),
  });
  const location = authCurrentLocation();
  const currentPath = location.pathname + location.searchStr;
  const shouldRedirectBack =
    currentPath &&
    currentPath !== "/" &&
    !location.pathname.startsWith("/auth/");
  if (shouldRedirectBack) {
    saveAuthRedirect(currentPath);
  }
  redirectToSignIn(shouldRedirectBack ? currentPath : undefined);
}

// these codes mean the session is unrecoverable: a refresh
// attempt cannot fix them, so log out immediately
function isTerminalAuthCode(code: string | null): boolean {
  return (
    code === "USER_INACTIVE" ||
    code === "REFRESH_TOKEN_MISSING" ||
    code === "REFRESH_TOKEN_INVALID" ||
    code === "USER_NOT_FOUND_FOR_REFRESH"
  );
}

// the client parses an error response itself, so reading it here needs a copy.
// mirrors what the client does with the original: json when it parses, the
// raw text when it does not
async function readErrorBody(response: Response): Promise<unknown> {
  const text = await response.clone().text();
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// a plain 401 means the access token expired, which a refresh can recover.
// the terminal codes say the session itself is gone, and no refresh fixes that
function isExpiredAccessToken(body: unknown): boolean {
  return (
    !!body &&
    typeof body === "object" &&
    (body as { detail?: unknown }).detail === "Unauthorized" &&
    !isTerminalAuthCode(extractApiExceptionCodeFromData(body))
  );
}

// logs out when a 401 is unrecoverable, once per lost session. the replay
// happens in the response interceptor, so an error that reaches this was either
// never replayable or was replayed and failed again.
//
// the token refresh call never logs out on its own 401: every refresh is
// awaited by a request, which gets that failure back along with a 401 of its
// own. and a request issued before the last sign-out belongs to the session
// that sign-out already ended, like the rest of a burst that failed together
function handleUnauthorized(
  error: ApiError,
  request: Request | undefined,
): void {
  if (
    request &&
    (isTokenRefresh(request) || issuedBeforeLastSignOut(request))
  ) {
    return;
  }
  if (isExpiredAccessToken(error.data) || isTerminalAuthCode(error.code)) {
    clearAuthAndRedirect();
  }
}

// a new terms version can go live mid-session, in which case the block
// surfaces as a 403 on an ordinary request rather than on the /me loader
function redirectToTermsIfRequired(error: ApiError): boolean {
  if (error.status !== 403 || error.code !== "TERMS_NOT_ACCEPTED") {
    return false;
  }
  const { pathname, searchStr } = authCurrentLocation();
  if (!pathname.startsWith("/auth/")) {
    saveAuthRedirect(pathname + searchStr);
    redirectToTerms();
  }
  return true;
}

function notifyResponseError(error: ApiError): void {
  const { status, data } = error;

  if (status === 403 && isMissingPermissionsError(error)) {
    addToast({
      title: m.toast_missing_permissions(),
      description: m.toast_missing_permissions_description(),
      severity: "danger",
      error,
    });
    return;
  }

  if (status === 422 && isHttpValidationError(data)) {
    addToast({
      title: m.toast_validation_error(),
      description: m.toast_validation_error_description(),
      severity: "danger",
      error,
    });
    return;
  }

  if (status === 503 && isServiceUnavailableApiExceptionResponse(data)) {
    addToast({
      title: m.toast_service_unavailable(),
      description: m.toast_service_unavailable_description_$service({
        service: data.service,
      }),
      severity: "danger",
      error,
    });
    return;
  }

  if (status !== 429) {
    return;
  }

  if (handleUsageLimitExceededError(error)) {
    return;
  }

  // covers both the app-level auth throttle and any edge/WAF rate limit,
  // which can surface a 429 on any route
  addToast({
    title: m.toast_too_many_attempts(),
    description: m.toast_too_many_attempts_description(),
    severity: "danger",
    error,
  });
}

// the replay has to live here rather than in the error interceptor: whatever an
// error interceptor returns is treated as another error, while a response
// interceptor may hand back a replacement response
client.interceptors.response.use(async (response, request, options) => {
  if (response.status !== 401 || isTokenRefresh(request)) {
    return response;
  }

  const replay = issued.get(request)?.replay;
  if (!replay || !isExpiredAccessToken(await readErrorBody(response))) {
    return response;
  }

  // the 401 proves the token is invalid even if its recorded expiry claims
  // otherwise: force a refresh, then replay
  try {
    await ensureFreshToken({ force: true });
  } catch {
    // hand the 401 back, and the error interceptor logs out on it
    return response;
  }
  // straight to the transport, so a second 401 lands on the error path
  // instead of looping back in here. through the request's own fetch, so a
  // replay keeps whatever the caller wrapped it in (a timeout)
  try {
    return await (options.fetch ?? fetch)(replay);
  } catch (error) {
    // the replay is a request of its own. left alone, the client would pair a
    // replay that never got an answer with the first attempt's 401
    throw isAbortError(error) ? error : toApiError(error);
  }
});

client.interceptors.error.use((rawError, response, request) => {
  // an aborted request never became an api failure, and whoever aborted it
  // recognises the abort by its own shape
  if (isAbortError(rawError)) return rawError;

  const error = toApiError(rawError, response);

  if (error.status === 401) {
    handleUnauthorized(error, request);
  } else if (!redirectToTermsIfRequired(error)) {
    if (request && error.status === 422 && isHttpValidationError(error.data)) {
      reportValidationError(request, error.data);
    }
    notifyResponseError(error);
  }

  return error;
});

export { client as apiClient } from "@/openapi/client.gen";
